// Sidebar.js
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaCamera, FaEye, FaQuestionCircle, FaWatchmanMonitoring, FaExclamationTriangle, FaCog, FaTicketAlt, FaUsers, FaUser, FaSignOutAlt, FaShieldAlt } from "react-icons/fa";
import { hasAdminAccess, isAdmin, AUTH_UPDATE_EVENT, clearAuth, getLogoutRedirectUrl } from "../utils/authUtils";
import { logout as apiLogout } from "../Api";
import "./Sidebar.css";

const Sidebar = () => {
  const { pathname } = useLocation();
  const [showAdmin, setShowAdmin] = useState(() => isAdmin());

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const evaluateAdminAccess = async () => {
      try {
        const canAccess = await hasAdminAccess();
        if (!cancelled) {
          setShowAdmin(canAccess);
        }
      } catch (error) {
        console.error('[Sidebar] Failed to evaluate admin access:', error);
        if (!cancelled) {
          setShowAdmin(false);
        }
      }
    };

    const handleStorageChange = (event) => {
      if (!event) return;
      const key = event.key || "";
      if (key === "userRoles" || key.startsWith("roleMetadata:")) {
        evaluateAdminAccess();
      }
    };

    evaluateAdminAccess();
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(AUTH_UPDATE_EVENT, evaluateAdminAccess);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(AUTH_UPDATE_EVENT, evaluateAdminAccess);
    };
  }, [pathname]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    try {
      await apiLogout();
    } catch (error) {
      console.error('[Sidebar] Logout request failed:', error);
    } finally {
      clearAuth();
      const redirectUrl = getLogoutRedirectUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        window.location.reload();
      }
    }
  };

  console.log('[Sidebar] Render:', { pathname, showAdmin });

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo-icon">
          <img src="/logonew.svg" alt="Logo" className="sidebar-logo-img" />
        </div>
        <span>White Tiger</span>
      </div>

      <NavLink
        to="/"
        className={`nav-btn ${pathname === "/" ? "active" : ""}`}
        end
      >
        <FaCamera className="nav-icon" />
        Start Analysis
      </NavLink>

      <NavLink
        to="/view"
        className={`nav-btn ${pathname === "/view" ? "active" : ""}`}
      >
        <FaEye className="nav-icon" />
        View Cameras
      </NavLink>

      <NavLink
        to="/logs"
        className={`nav-btn ${pathname === "/logs" ? "active" : ""}`}
      >
        <FaExclamationTriangle className="nav-icon" />
        Alert Logs
      </NavLink>

      <NavLink
        to="/analytics"
        className={`nav-btn ${pathname === "/analytics" ? "active" : ""}`}
      >
        <FaWatchmanMonitoring className="nav-icon" />
        Analytics
      </NavLink>

      <NavLink to="/ask" className={`nav-btn ${pathname === "/ask" ? "active" : ""}`}>
        <FaQuestionCircle className="nav-icon" />
        Ask&nbsp;a&nbsp;Question
      </NavLink>

      <NavLink
        to="/profile"
        className={`nav-btn ${pathname === "/profile" ? "active" : ""}`}
      >
        <FaUser className="nav-icon" />
        Profile
      </NavLink>

      {/* Admin Section */}
      {showAdmin && (
        <>
          <div className="nav-section-divider">Admin</div>
          <NavLink
            to="/admin/organization"
            className={`nav-btn ${pathname === "/admin/organization" ? "active" : ""}`}
          >
            <FaCog className="nav-icon" />
            Organization
          </NavLink>
          <NavLink
            to="/admin/invitations"
            className={`nav-btn ${pathname === "/admin/invitations" ? "active" : ""}`}
          >
            <FaTicketAlt className="nav-icon" />
            Invitations
          </NavLink>
          <NavLink
            to="/admin/users"
            className={`nav-btn ${pathname === "/admin/users" ? "active" : ""}`}
          >
            <FaUsers className="nav-icon" />
            Users
          </NavLink>
        </>
      )}

      <button
        type="button"
        className="nav-btn nav-btn-logout"
        onClick={handleLogout}
        disabled={isLoggingOut}
      >
        <FaSignOutAlt className="nav-icon" />
        {isLoggingOut ? "Logging out..." : "Logout"}
      </button>
    </aside>
  );
};

export default Sidebar;
